import { ImageSliderAnalyticsEvent, ImageSliderCard } from '@liveauctioneers/hammer-ui-core/imageSlider';
import { WebRTCPlayer } from '@liveauctioneers/hammer-ui-core/webRTCPlayer';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAppSelector } from '@/redux/hooks';
import { getAntMediaHostname } from '@/redux/modules/config';
import { getLocale } from '@/redux/modules/intl';
import {
    getItemSummaryCatalogId,
    getItemSummaryImageVersion,
    getItemSummaryPhotos,
    getItemSummarySellerId,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getStreamStatus, hasVideoStream } from '@/redux/modules/video';
import { useTrackItemImageGalleryInteractionAnalytics } from '@/utils/amplitude/analytics/item/trackItemImageGalleryInteraction.analytics';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { popupCenter } from '@/utils/popupCenter';
import { getConsoleImageCarouselUrl, getImageUrl } from '@/utils/urls';

import { BiddingConsoleExpandedGalleryPip } from './BiddingConsoleExpandedGalleryPip';
import { BiddingConsoleGalleryImageLightboxButton } from './BiddingConsoleGalleryImageLightboxButton';
import { BiddingConsoleGalleryVideoPip } from './BiddingConsoleGalleryVideoPip';
import { BiddingConsoleMainImageSlider } from './BiddingConsoleMainImageSlider';

const IMAGE_CAROUSEL_POPUP_HEIGHT = 1000;
const IMAGE_CAROUSEL_POPUP_WIDTH = 1000;

/** Matches `consoleThreeColumn` in tailwind.config.mjs */
const THREE_COLUMN_MEDIA_QUERY = '(min-width: 1400px)';

/** Widen typing: some environments omit `addEventListener` on `MediaQueryList` (legacy). */
type MediaQueryListCompat = {
    addEventListener?: (type: 'change', listener: () => void) => void;
    addListener?: (listener: () => void) => void;
    matches: boolean;
    removeEventListener?: (type: 'change', listener: () => void) => void;
    removeListener?: (listener: () => void) => void;
};

const MAIN_SLIDER_DESKTOP_IMAGE = 450;
const MAIN_SLIDER_MOBILE_IMAGE = 366;

const PIP_COLUMN_WIDTH_PX = 171;
/** Matches `gap-x-sm` (16px) between main image and PIP in sidecar. */
const PIP_SIDECAR_GAP_PX = 16;
/** Reserved inline-end space for PIP + gutter (`ml-auto` + this margin mirrors old grid sidecar). */
const PIP_SIDECAR_IMAGE_MARGIN_END_PX = PIP_COLUMN_WIDTH_PX + PIP_SIDECAR_GAP_PX;
/** Minimum measured width to use sidecar: max image column + PIP column + both gutters. */
const PIP_SIDE_BY_SIDE_MIN_WIDTH_PX = MAIN_SLIDER_DESKTOP_IMAGE + PIP_COLUMN_WIDTH_PX + 2 * PIP_SIDECAR_GAP_PX;
/** Width at or above this switches overlay → sidecar. */
const PIP_SIDECAR_ENTER_MIN_WIDTH_PX = PIP_SIDE_BY_SIDE_MIN_WIDTH_PX;
/**
 * Stay in sidecar until width drops below this (narrow hysteresis only; unified DOM avoids
 * scrollbar/reflow oscillation that required a wide band with the old grid swap).
 */
const PIP_SIDECAR_EXIT_MIN_WIDTH_PX = PIP_SIDECAR_ENTER_MIN_WIDTH_PX - 30;
/** Matches `right-xs` (8px) inset from the centered image’s right edge in overlay mode. */
const PIP_OVERLAY_INSET_FROM_IMAGE_EDGE_PX = 8;

type Props = {
    className?: string;
    itemId: number;
};

export const BiddingConsoleImageSlider = ({ className, itemId }: Props) => {
    const formatMessage = useAppFormatMessage();
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const imageVersion = useAppSelector((state) => getItemSummaryImageVersion(state, itemId));
    const photos = useAppSelector((state) => getItemSummaryPhotos(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const locale = useAppSelector(getLocale);
    const { streamId } = useAppSelector((state) => getStreamStatus(state, catalogId));
    const catalogHasVideoStream = useAppSelector((state) => hasVideoStream(state, catalogId));
    const antMediaHostname = useAppSelector(getAntMediaHostname);

    const localePath = locale === 'en-gb' ? `/${locale}` : '';
    const trackImageGalleryInteraction = useTrackItemImageGalleryInteractionAnalytics();
    const [imageGalleryIndex, setImageGalleryIndex] = useState(0);
    const [activeView, setActiveView] = useState<'gallery' | 'video'>('gallery');
    const [isThreeColumn, setIsThreeColumn] = useState(() => window.matchMedia(THREE_COLUMN_MEDIA_QUERY).matches);
    const [usePipSidecarLayout, setUsePipSidecarLayout] = useState(false);
    const usePipSidecarLayoutRef = useRef(false);

    const layoutMeasureRef = useRef<HTMLDivElement>(null);

    const enlargeVideoTooltipId = `bidding-console-enlarge-video-${itemId}`;
    const enlargeImageTooltipId = `bidding-console-enlarge-image-${itemId}`;

    useEffect(() => {
        setImageGalleryIndex(0);
    }, [itemId]);

    /**
     * `consoleThreeColumn` breakpoint: standards `MediaQueryList#addEventListener('change')`.
     * Legacy `addListener` / `removeListener` only for engines without `addEventListener` on MQL.
     */
    useEffect(() => {
        const mediaQueryList = window.matchMedia(THREE_COLUMN_MEDIA_QUERY) as unknown as MediaQueryListCompat;
        const syncViewForLayout = () => {
            setIsThreeColumn(mediaQueryList.matches);
            if (mediaQueryList.matches) {
                setActiveView('gallery');
            }
        };
        syncViewForLayout();
        mediaQueryList.addEventListener('change', syncViewForLayout);
        return () => {
            mediaQueryList.removeEventListener?.('change', syncViewForLayout);
        };
    }, []);

    useEffect(() => {
        usePipSidecarLayoutRef.current = usePipSidecarLayout;
    }, [usePipSidecarLayout]);

    const showExpandedVideo = catalogHasVideoStream && activeView === 'video';
    const pipInlineVisible = !isThreeColumn && catalogHasVideoStream;

    useEffect(() => {
        if (!pipInlineVisible) {
            usePipSidecarLayoutRef.current = false;
            setUsePipSidecarLayout(false);
            return;
        }

        const el = layoutMeasureRef.current;
        if (!el) {
            return;
        }

        const measure = () => {
            const width = el.getBoundingClientRect().width;
            const wasSidecar = usePipSidecarLayoutRef.current;
            const next = wasSidecar ? width >= PIP_SIDECAR_EXIT_MIN_WIDTH_PX : width >= PIP_SIDECAR_ENTER_MIN_WIDTH_PX;
            const layoutWouldChange = next !== wasSidecar;
            if (layoutWouldChange) {
                usePipSidecarLayoutRef.current = next;
                setUsePipSidecarLayout(next);
            }
        };

        measure();

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(el);
        return () => {
            resizeObserver.disconnect();
        };
    }, [pipInlineVisible, showExpandedVideo, activeView]);

    const handleImageGalleryAnalytics = useCallback(
        (event: ImageSliderAnalyticsEvent) => {
            setImageGalleryIndex(event.imagePosition - 1);
            trackImageGalleryInteraction(event);
        },
        [trackImageGalleryInteraction]
    );

    const openImageCarouselPopout = useCallback(
        (indexOverride?: number) => {
            const index = indexOverride ?? imageGalleryIndex;
            const imageNum = index + 1;
            trackImageGalleryInteraction({
                imagePosition: imageNum,
                interactionType: 'Open Lightbox',
                totalImages: photos.length,
            });
            popupCenter({
                height: IMAGE_CAROUSEL_POPUP_HEIGHT,
                title: `${title} Image`,
                url: localePath + getConsoleImageCarouselUrl(catalogId, itemId, imageNum),
                width: IMAGE_CAROUSEL_POPUP_WIDTH,
            });
        },
        [catalogId, itemId, localePath, photos.length, title, trackImageGalleryInteraction, imageGalleryIndex]
    );

    const images = useMemo(() => {
        const imageGalleryImages: ImageSliderCard[] = [];

        photos.forEach((photo, index) => {
            const alt = index ? `${title} - ${index + 1}` : title;
            const src = getImageUrl({
                catalogId,
                imageNumber: photo,
                imageVersion,
                itemId,
                quality: 80,
                sellerId,
                sharpen: true,
            });

            imageGalleryImages.push({
                imageData: {
                    alt: alt,
                    baseUrl: src,
                    title,
                },
                key: `responsive-image-${index + 1}`,
            });
        });

        return imageGalleryImages;
    }, [photos, title, catalogId, imageVersion, itemId, sellerId]);

    const enlargeVideoLabel = formatMessage('bidding_console', 'console', 'enlargeVideo');
    const enlargeImageLabel = formatMessage('bidding_console', 'console', 'enlargeImage');

    const handleOpenGalleryLightbox = useCallback(() => {
        openImageCarouselPopout();
        handleImageGalleryAnalytics({
            imagePosition: imageGalleryIndex + 1,
            interactionType: 'Open Lightbox',
            totalImages: photos.length,
        });
    }, [openImageCarouselPopout, handleImageGalleryAnalytics, imageGalleryIndex, photos.length]);

    const mainImageSlider = (
        <BiddingConsoleMainImageSlider
            cards={images}
            contentKey={itemId}
            desktopImageSize={MAIN_SLIDER_DESKTOP_IMAGE}
            mobileImageSize={MAIN_SLIDER_MOBILE_IMAGE}
            onAnalyticsEventTriggered={handleImageGalleryAnalytics}
        />
    );

    const pipPreviewImage = images[0]?.imageData;

    const showGalleryVideoPip = pipInlineVisible && activeView === 'gallery';

    const galleryImageLightboxButton = <BiddingConsoleGalleryImageLightboxButton onOpen={handleOpenGalleryLightbox} />;

    return (
        <div
            ref={layoutMeasureRef}
            className={cn('relative w-full', className)}
            data-testid="pip-layout-measure"
        >
            {!showExpandedVideo && (
                <>
                    {showGalleryVideoPip ? (
                        <>
                            <div
                                className={cn('relative max-w-[450px]', usePipSidecarLayout ? 'ml-auto' : 'mx-auto')}
                                data-pip-layout={usePipSidecarLayout ? 'sidecar' : 'overlay'}
                                style={
                                    usePipSidecarLayout ? { marginRight: PIP_SIDECAR_IMAGE_MARGIN_END_PX } : undefined
                                }
                            >
                                <div className="flex justify-center">{mainImageSlider}</div>
                                {!usePipSidecarLayout && galleryImageLightboxButton}
                            </div>
                            <div
                                className="absolute top-xs z-[15] w-[171px] transition-[right] duration-200 ease-out"
                                style={{
                                    right: usePipSidecarLayout
                                        ? 0
                                        : `calc((100% - ${MAIN_SLIDER_DESKTOP_IMAGE}px) / 2 + ${PIP_OVERLAY_INSET_FROM_IMAGE_EDGE_PX}px)`,
                                }}
                            >
                                <BiddingConsoleGalleryVideoPip
                                    antMediaHostname={antMediaHostname}
                                    enlargeVideoLabel={enlargeVideoLabel}
                                    onEnlargeVideo={() => {
                                        setActiveView('video');
                                    }}
                                    streamId={streamId}
                                    tooltipId={enlargeVideoTooltipId}
                                />
                            </div>
                            {usePipSidecarLayout && galleryImageLightboxButton}
                        </>
                    ) : (
                        <div className="relative mx-auto max-w-[450px]">
                            <div className="flex justify-center">{mainImageSlider}</div>
                            {galleryImageLightboxButton}
                        </div>
                    )}
                </>
            )}
            {showExpandedVideo && (
                <div className="relative w-full">
                    <div
                        className={cn('relative max-w-[450px]', usePipSidecarLayout ? 'ml-auto' : 'mx-auto')}
                        data-pip-layout={usePipSidecarLayout ? 'sidecar' : 'overlay'}
                        style={usePipSidecarLayout ? { marginRight: PIP_SIDECAR_IMAGE_MARGIN_END_PX } : undefined}
                    >
                        <WebRTCPlayer
                            className="aspect-video w-full rounded-sm"
                            debug={false}
                            hostname={antMediaHostname}
                            streamId={streamId}
                        />
                    </div>
                    <div
                        className="absolute top-xs z-[15] w-[171px] transition-[right] duration-200 ease-out consoleThreeColumn:hidden"
                        style={{
                            right: usePipSidecarLayout
                                ? 0
                                : `calc((100% - ${MAIN_SLIDER_DESKTOP_IMAGE}px) / 2 + ${PIP_OVERLAY_INSET_FROM_IMAGE_EDGE_PX}px)`,
                        }}
                    >
                        <BiddingConsoleExpandedGalleryPip
                            enlargeImageLabel={enlargeImageLabel}
                            onEnlargeImage={() => {
                                setActiveView('gallery');
                            }}
                            previewImage={pipPreviewImage}
                            tooltipId={enlargeImageTooltipId}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
